import { skipToken } from "@reduxjs/toolkit/query";
import { getIsTrainingProject } from "@repo/app-types/helpers/utilities";
import type { BreadcrumbItem } from "@repo/ui/Navigation/breadcrumbs";
import { useProjectByIdQuery } from "../../api/entities/project.js";
import { appPaths } from "../appPaths.js";
import { CrumbLabelSkeleton } from "./crumbLabel.js";
import { readStoredWorkspaceId } from "./rememberedWorkspace.js";
import { WorkspaceId } from "./workspaces.js";

/**
 * The workspace crumb ("Trainings" vs "Job openings") for a project page.
 * Both its label and its link target follow the fetched project's type, so it
 * can't come from a static route handle; the breadcrumbs container prepends
 * it to the project routes' handle-driven trail. Shares the project fetch
 * with the project-name crumb through the query cache.
 */
export function useProjectWorkspaceCrumbItem(
  projectId: string | undefined,
): BreadcrumbItem | null {
  const result = useProjectByIdQuery(projectId ?? skipToken);

  if (projectId === undefined) {
    return null;
  }
  if (result.data) {
    return buildWorkspaceItem(getIsTrainingProject(result.data));
  }
  if (result.isError) {
    // Fall back to the most recently used project workspace: the entry point
    // is usually the matching list, so it tends to agree.
    return buildWorkspaceItem(readStoredWorkspaceId() === WorkspaceId.Training);
  }
  return { label: <CrumbLabelSkeleton /> };
}

function buildWorkspaceItem(isTrainingProject: boolean): BreadcrumbItem {
  return isTrainingProject
    ? { label: "Trainings", href: appPaths.trainings.build() }
    : { label: "Job openings", href: appPaths.home.build() };
}
